import {
    Box,
    Flex,
    VStack,
    useColorModeValue
  } from "@chakra-ui/react";
  import { useEffect, useRef, useState } from "react";
  import { FormInput } from "../form/controls/form.input";
  import { useOutsideClick } from "../hooks/hooks/click-outside.hook";
  import { companyService } from "../../service/company/company.service";
  import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
  
  const GlobalSearch = () => {
    const navigate = useNavigate();
    const { region } = useSelector((state: RootState) => state.userProfile);

    const wrapperRef = useRef(null);
    const [searchParams] = useSearchParams();
    const [searchTerm, setSearchTerm] = useState(null);
    const [searchDropdownOpen, setSearchDropdownOpen] = useState(false);
    const [hasSearchParams, setHasSearchParams] = useState(false);
    const [isClearing, setIsClearing] = useState(false);
    
    const [globalSearchResults, setGlobalSearchResults] = useState(
      [] as { title: string; type: "job" | "inventory" | "branch" | "company"; id: string }[]
    );
    
    const [inventorySearchResults, setInventorySearchResults] = useState(
      [] as { title: string; id: string; job_id: string }[]
    );
    const [branchSearchResults, setBranchSearchResults] = useState(
      [] as { title: string; id: string }[]
    );
    const [companySearchResults, setCompanySearchResults] = useState(
      [] as { title: string; id: string }[]
    );
    const [jobSearchResults, setJobSearchResults] = useState(
      [] as { title: string; id: string }[]
    );
  
    // Background color for the dropdown
    const bgColor = useColorModeValue('white', 'gray.800');
  
    useEffect(() => {
      const term = searchParams.get("searchTerm");
      const inventoryId = searchParams.get("inventory_id");
  
      if (term) {
        setSearchTerm(term);
      }
  
      setHasSearchParams(!!term || !!inventoryId);
    }, [searchParams]);
  
    useOutsideClick(wrapperRef, () => {
      setSearchDropdownOpen(false);
    });
  
    const clear = async () => {
      setIsClearing(true);
      setSearchTerm("");
      setSearchDropdownOpen(false);
      searchParams.delete("searchTerm");
      searchParams.delete("inventory_id");
      setHasSearchParams(false);
      navigate(window.location.pathname);
  
      // reload window after 1 second delay
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    };
  
    const handleGlobalSearch = async (e) => {
      const { value } = e.target;
  
      setSearchTerm(value);
  
      if (!value || value.length < 2) {
        setSearchDropdownOpen(false);
        return;
      }
  
      await companyService.globalSearch({term: value, region}).then((res) => {
        setBranchSearchResults(res.data.filter((item) => item.type === "branch"));
        setInventorySearchResults(
          res.data.filter((item) => item.type === "inventory")
        );
        setJobSearchResults(res.data.filter((item) => item.type === "job"));
        setCompanySearchResults(
          res.data.filter((item) => item.type === "company")
        );
        setGlobalSearchResults(res.data);
      });
  
      setSearchDropdownOpen(true);
    };
  
    const getEntityColor = (type) => {
      switch (type) {
        case "job":
          return "#8BBFEA";
        case "inventory":
          return "#A0CCA2";
        case "branch":
        case "company":
          return "#F6D981";
        default:
          return "#F6D981";
      }
    };
  
    const navigateToResult = (path, id) => {
      window.location.href = `${path}/${id}?searchTerm=${searchTerm}`;
    };
  
    return (
      <Box ref={wrapperRef} w="600px" pos="relative">
        <FormInput
          autoFocus={true}
          name={"globalSearch"}
          value={searchTerm}
          placeholder={isClearing ? "Clearing..." : "Start typing to find your jobs, inventory, and more..."}
          onChange={handleGlobalSearch}
          rightAddon={
            <Flex
              hidden={!hasSearchParams || isClearing}
              ml="4px"
              justifyContent="center"
              cursor="pointer"
              alignItems="center"
              w="80px"
              bg="brand.green"
              borderRadius="8px"
              color="white"
              fontWeight="600"
              onClick={clear}
            >
              Clear
            </Flex>
          }
          onFocus={(e) => {
            if (!!searchTerm?.length) handleGlobalSearch(e);
          }}
        />
  
        {searchDropdownOpen && (
          <VStack
            pos="absolute"
            w="100%"
            align="stretch"
            mt="2"
            boxShadow="sm"
            border="1px solid #E2E8F0"
            borderRadius="md"
            bg={bgColor}
            px="3"
            py="4"
            zIndex={1000}
          >
            {globalSearchResults.length ? (
              <>
                {/* Company Results */}
                {companySearchResults.length > 0 && (
                  <>
                    {companySearchResults.length === 5 && (
                      <Flex
                        fontSize="12px"
                        justifyContent="flex-end"
                        color="gray.500"
                      >
                        Top 5 showing
                      </Flex>
                    )}
                    {companySearchResults.map((item, index) => (
                      <Flex
                        key={item.id}
                        py="1"
                        px="2"
                        borderBottom={
                          index === companySearchResults.length - 1
                            ? "1px solid #E2E8F0"
                            : ""
                        }
                        flexDir="row"
                        justifyContent="space-between"
                        _hover={{
                          cursor: "pointer",
                          bg: "gray.200",
                        }}
                        borderRadius="4px"
                        onClick={() => navigateToResult("/app/company", item.id)}
                      >
                        <Box>{item.title}</Box>
                        <Box
                          fontSize="12px"
                          fontWeight="600"
                          h="20px"
                          px="12px"
                          bg={getEntityColor("company")}
                          borderRadius="6px"
                        >
                          Company
                        </Box>
                      </Flex>
                    ))}
                  </>
                )}
  
                {/* Branch Results */}
                {branchSearchResults.length > 0 && (
                  <>
                    {branchSearchResults.length === 5 && (
                      <Flex
                        fontSize="12px"
                        justifyContent="flex-end"
                        color="gray.500"
                      >
                        Top 5 showing
                      </Flex>
                    )}
                    {branchSearchResults.map((item, index) => (
                      <Flex
                        key={item.id}
                        py="1"
                        px="2"
                        borderBottom={
                          index === branchSearchResults.length - 1
                            ? "1px solid #E2E8F0"
                            : ""
                        }
                        flexDir="row"
                        justifyContent="space-between"
                        _hover={{
                          cursor: "pointer",
                          bg: "gray.200",
                        }}
                        borderRadius="4px"
                        onClick={() => navigateToResult("/app/company", item.id)}
                      >
                        <Box>{item.title}</Box>
                        <Box
                          fontSize="12px"
                          fontWeight="600"
                          h="20px"
                          px="12px"
                          bg={getEntityColor("branch")}
                          borderRadius="6px"
                        >
                          Branch
                        </Box>
                      </Flex>
                    ))}
                  </>
                )}
  
                {/* Job Results */}
                {jobSearchResults.length > 0 && (
                  <>
                    {jobSearchResults.length === 5 && (
                      <Flex
                        fontSize="12px"
                        justifyContent="flex-end"
                        color="gray.500"
                      >
                        Top 5 showing
                      </Flex>
                    )}
                    {jobSearchResults.map((item, index) => (
                      <Flex
                        key={item.id}
                        py="1"
                        px="2"
                        flexDir="row"
                        justifyContent="space-between"
                        borderBottom={
                          index === jobSearchResults.length - 1
                            ? "1px solid #E2E8F0"
                            : ""
                        }
                        _hover={{
                          cursor: "pointer",
                          bg: "gray.200",
                        }}
                        borderRadius="4px"
                        onClick={() => navigateToResult("/app/job", item.id)}
                      >
                        <Box>JOB-{item.title}</Box>
                        <Box
                          fontSize="12px"
                          fontWeight="600"
                          h="20px"
                          px="12px"
                          bg={getEntityColor("job")}
                          borderRadius="6px"
                        >
                          Job
                        </Box>
                      </Flex>
                    ))}
                  </>
                )}
  
                {/* Inventory Results */}
                {inventorySearchResults.length > 0 && (
                  <>
                    <Flex
                      cursor="pointer"
                      fontSize="12px"
                      justifyContent="flex-end"
                      _hover={{ textDecoration: "underline" }}
                      onClick={() => {
                        window.location.href = `/app/inventory?searchTerm=${searchTerm}`;
                      }}
                    >
                      View filtered results
                    </Flex>
                    {inventorySearchResults.map((item) => (
                      <Flex
                        key={item.id}
                        py="1"
                        px="2"
                        flexDir="row"
                        justifyContent="space-between"
                        _hover={{
                          cursor: "pointer",
                          bg: "gray.200",
                        }}
                        borderRadius="4px"
                        onClick={() => {
                          const module = item.title.endsWith('SOLD') ? 'archive' : 'inventory';
                          const toggle = item.title.endsWith('COMPLETE') ? 'showComplete=true' : null;
                          window.location.href = `/app/${module}?inventory_id=${item.id}&searchTerm=${searchTerm}${toggle ? `&${toggle}` : ''}`;
                        }}
                      >
                        <Box>#QR_{item.title}</Box>
                        <Box
                          fontSize="12px"
                          fontWeight="600"
                          h="20px"
                          px="12px"
                          bg={getEntityColor("inventory")}
                          borderRadius="6px"
                        >
                          {item.title.endsWith('SOLD') ? 'archive' : 'inventory'}
                        </Box>
                      </Flex>
                    ))}
                  </>
                )}
              </>
            ) : (
              <Box p="2" textAlign="center" color="gray.500">
                No results found
              </Box>
            )}
          </VStack>
        )}
      </Box>
    );
  };
  
  export default GlobalSearch;